import { API_BASE_URL } from './client';
import { consumeChatNdjsonStream } from './streamNdjson';

function authHeaders(keycloak, extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  if (keycloak?.token) headers.Authorization = `Bearer ${keycloak.token}`;
  return headers;
}

async function readError(response) {
  const body = await response.json().catch(() => ({}));
  const detail = body.detail;
  if (typeof detail === 'string') return detail;
  if (detail && detail.message) return detail.message;
  return 'Request failed';
}

export async function getGuestSharePolicy(keycloak) {
  const response = await fetch(`${API_BASE_URL}/chat-shares/guest-policy/`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function searchShareUsers(keycloak, assistantId, q) {
  const params = new URLSearchParams({ assistant_id: String(assistantId), q });
  const response = await fetch(`${API_BASE_URL}/chat-shares/users/?${params.toString()}`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function getChatShare(keycloak, assistantId, sessionId) {
  const response = await fetch(
    `${API_BASE_URL}/chat-shares/assistants/${encodeURIComponent(assistantId)}/?session_id=${encodeURIComponent(sessionId)}`,
    { headers: authHeaders(keycloak) },
  );
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function saveChatShare(keycloak, assistantId, body) {
  const response = await fetch(`${API_BASE_URL}/chat-shares/assistants/${encodeURIComponent(assistantId)}/`, {
    method: 'PUT',
    headers: authHeaders(keycloak),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function updateChatShareSnapshot(keycloak, assistantId, sessionId) {
  const response = await fetch(
    `${API_BASE_URL}/chat-shares/assistants/${encodeURIComponent(assistantId)}/update-snapshot/?session_id=${encodeURIComponent(sessionId)}`,
    { method: 'POST', headers: authHeaders(keycloak) },
  );
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function regenerateChatShare(keycloak, assistantId, sessionId) {
  const response = await fetch(
    `${API_BASE_URL}/chat-shares/assistants/${encodeURIComponent(assistantId)}/regenerate/?session_id=${encodeURIComponent(sessionId)}`,
    { method: 'POST', headers: authHeaders(keycloak) },
  );
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function listAssistantShares(keycloak, assistantId) {
  const response = await fetch(
    `${API_BASE_URL}/chat-shares/assistants/${encodeURIComponent(assistantId)}/list/`,
    { headers: authHeaders(keycloak) },
  );
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function getShareReview(keycloak, publicId) {
  const response = await fetch(`${API_BASE_URL}/chat-shares/${encodeURIComponent(publicId)}/review/`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function closeShareContinuation(keycloak, publicId, continuationId) {
  const response = await fetch(
    `${API_BASE_URL}/chat-shares/${encodeURIComponent(publicId)}/continuations/${continuationId}/close/`,
    { method: 'POST', headers: authHeaders(keycloak) },
  );
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function startNewPublicShare(token, keycloak) {
  const response = await fetch(`${API_BASE_URL}/chat-shares/public/${encodeURIComponent(token)}/new/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    credentials: 'include',
  });
  if (response.status === 404) {
    const error = new Error('Not found');
    error.status = 404;
    throw error;
  }
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function readPublicShare(token, keycloak) {
  const response = await fetch(`${API_BASE_URL}/chat-shares/public/${encodeURIComponent(token)}/`, {
    headers: authHeaders(keycloak),
    credentials: 'include',
  });
  if (response.status === 404) return { state: 'not_found' };
  if (response.status === 429) return { state: 'limited' };
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}

export async function sendPublicShare(token, keycloak, body, handlers) {
  const response = await fetch(`${API_BASE_URL}/chat-shares/public/${encodeURIComponent(token)}/messages/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await readError(response));
  if (!response.headers.get('content-type')?.includes('ndjson') && !response.body) {
    return response.json();
  }
  await consumeChatNdjsonStream(response, handlers);
}
